import { useVariant } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { BrandLogo } from '@calendly/ui/components/brand-logo';
import { Button } from '@calendly/ui/components/button';
import { FixedContainer } from '@calendly/ui/components/fixed-container';
import { Link } from '@calendly/ui/components/link';
import { Sheet } from '@calendly/ui/components/sheet';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useGetUserQuery, useUpdateUserGrowthExperimentsMutation } from '../../../common';
import { TRACKING_EVENT_NAMES, useTrack, useTrackOnce } from '../../../hooks/analytics_events';
import { ONBOARDING_PAYMENTS_EXPERIMENT_KEY, hasOnboardingPaymentsFlag, useOnboardingPaymentsFullSizeModalVisible } from '../../../hooks/growth_experiments/use-onboarding-payments-experiment';
const overlayCss = "o1wfbxfv";
const contentCss = "c1da7a4s";
const titleCss = "t1nb84s3";
const subtitleWrapperCss = "s1lidhin";
const cardCss = "c1qwcdwj";
const cardDescriptionCss = "cr8yrzi";
const buttonRowCss = "bivrk6m";
const skipTextCss = "sxs4ss6";
const buttonContentCss = "b19et0fj";
const buttonLogoCss = "b1doyjbh";
const OnboardingPaymentsModalContent = () => {
  const variant = useVariant(ONBOARDING_PAYMENTS_EXPERIMENT_KEY);
  const shouldShow = useOnboardingPaymentsFullSizeModalVisible(variant);
  const {
    data: userData
  } = useGetUserQuery();
  const [updateGrowthExperiments] = useUpdateUserGrowthExperimentsMutation();
  const history = useHistory();
  const location = useLocation();
  const track = useTrack();
  const [isDismissed, setIsDismissed] = useState(false);
  const userName = userData?.name || '';
  const hasUser = Boolean(userData?.uuid || userData?.id);
  const existingExperimentState = userData?.growth_experiments?.[ONBOARDING_PAYMENTS_EXPERIMENT_KEY] || {};
  const dismissalPayload = {
    [ONBOARDING_PAYMENTS_EXPERIMENT_KEY]: {
      ...existingExperimentState,
      full_page_modal_dismissed: true
    }
  };
  const modalTrackingProperties = {
    location: 'post_intro_flow',
    modal_name: 'collect_payments_setup',
    path: location.pathname
  };
  const dismissModal = () => {
    setIsDismissed(true);
  };
  useTrackOnce(hasUser && shouldShow && !isDismissed, {
    name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
    properties: {
      action: 'opening',
      ...modalTrackingProperties
    }
  });
  const trackModalInteraction = action => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action,
        ...modalTrackingProperties
      }
    });
  };
  const handleSkipClick = async () => {
    trackModalInteraction('dismissed');
    dismissModal();
    try {
      const updatePromise = updateGrowthExperiments(dismissalPayload);
      if (updatePromise?.unwrap) {
        await updatePromise.unwrap();
      } else {
        await updatePromise;
      }
    } catch (error) {
      // The mutation handles errors; no additional action.
    }
  };
  const handleIntegrationClick = (path, action) => async event => {
    event?.preventDefault();
    trackModalInteraction(action);
    dismissModal();
    try {
      await updateGrowthExperiments(dismissalPayload).unwrap();
      history.push(path);
    } catch (error) {
      // The mutation handles errors; no redirect on failure.
    }
  };
  if (!shouldShow || isDismissed) {
    return null;
  }
  return <FixedContainer className={overlayCss} data-testid="onboarding-payments-modal">
      <div className={contentCss}>
        <TitleText level="2" className={titleCss}>
          {I18n.t('publisher.growth_experiments.onboarding_payments.title', {
          name: userName
        })}
        </TitleText>
        <div className={subtitleWrapperCss}>
          <BodyText size="medium" weight="semibold">
            {I18n.t('publisher.growth_experiments.onboarding_payments.subtitle')}
          </BodyText>
        </div>
        <Sheet className={cardCss}>
          <TitleText level="4">
            {I18n.t('publisher.growth_experiments.onboarding_payments.section_title')}
          </TitleText>
          <BodyText size="small" className={cardDescriptionCss}>
            {I18n.t('publisher.growth_experiments.onboarding_payments.description')}
          </BodyText>
          <div className={buttonRowCss}>
            <Button decoration="secondary-outline" as="a" href="/integrations/stripe" onClick={handleIntegrationClick('/integrations/stripe', 'stripe_integration_page_opened')}>
              <span className={buttonContentCss}>
                <BrandLogo name="stripe" className={buttonLogoCss} />
                {I18n.t('publisher.growth_experiments.onboarding_payments.connect_stripe')}
              </span>
            </Button>
            <Button decoration="secondary-outline" as="a" href="/integrations/paypal" onClick={handleIntegrationClick('/integrations/paypal', 'paypal_integration_page_opened')}>
              <span className={buttonContentCss}>
                <BrandLogo name="pay-pal" className={buttonLogoCss} />
                {I18n.t('publisher.growth_experiments.onboarding_payments.connect_paypal')}
              </span>
            </Button>
          </div>
        </Sheet>
        <BodyText size="small" className={skipTextCss} weight="semibold">
          {I18n.t('publisher.growth_experiments.onboarding_payments.or')}
          {' '}
          <Link as="button" type="button" underline={false} onClick={handleSkipClick}>
            {I18n.t('publisher.growth_experiments.onboarding_payments.skip_to_dashboard')}
          </Link>
        </BodyText>
      </div>
    </FixedContainer>;
};
export const OnboardingPaymentsModal = () => hasOnboardingPaymentsFlag() ? <OnboardingPaymentsModalContent /> : null;

require("./onboarding_payments_modal.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./onboarding_payments_modal.js");